/**
 * KioskLinksPage · v2.7.60+E43 (2026-07-03)
 * ═════════════════════════════════════════════════════════════════════
 * §6 · Platform Deployment & 4K URL Provisioning Manager
 *
 * Admin console → Developer → Dispatch Video Wall → "Provisioned Links".
 * List / create / revoke / toggle long-lived, opaque-token-secured
 * kiosk URLs. Each URL embeds:
 *   ?token=<64-char>       — long-lived opaque token
 *   &res=1080p | 4k        — output resolution profile
 *   &hw=macos|chromeos|raspi|androidtv — hardware performance profile
 *   &zone=Z1..Z7           — optional default zone
 *
 * Revoking a link marks it inactive server-side; the token endpoint
 * returns 404 on the next kiosk heartbeat and the wall renders an
 * "Access Revoked" screen instead of the live grid.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Link2, Copy, RefreshCw, Trash2, Plus, Download, MonitorPlay,
} from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";

const HW_LABELS = {
  macos:     "macOS Desktop / Retina",
  chromeos:  "Chrome Kiosk / Chromebox",
  raspi:     "Raspberry Pi 4/5",
  androidtv: "Fire TV / Android TV",
};

const RES_LABELS = { "1080p": "1080p Full HD", "4k": "3840×2160 (4K UHD)" };

// v2.7.61+E44 · Heartbeat traffic-light thresholds. `last_ping_at` is
// bumped every time the physical kiosk validates its token, so a fresh
// ping means the wall is alive on that endpoint. Anything > 5min without
// a ping is treated as stalled / offline.
function heartbeatStatus(lastPingAt) {
  if (!lastPingAt) return { color: "#3f3f46", label: "Never", tone: "unseen" };
  const t = new Date(lastPingAt).getTime();
  if (Number.isNaN(t)) return { color: "#3f3f46", label: "Invalid", tone: "unseen" };
  const ageSec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (ageSec < 60) return { color: "#22c55e", label: `${ageSec}s`, tone: "live" };
  if (ageSec < 300) return { color: "#f59e0b", label: `${Math.round(ageSec / 60)}m`, tone: "stalling" };
  if (ageSec < 3600) return { color: "#ef4444", label: `${Math.round(ageSec / 60)}m`, tone: "dead" };
  return { color: "#7f1d1d", label: `${Math.round(ageSec / 3600)}h`, tone: "dead" };
}

function HeartbeatDot({ lastPingAt, testid }) {
  const s = useMemo(() => heartbeatStatus(lastPingAt), [lastPingAt]);
  return (
    <span
      data-testid={testid}
      data-heartbeat-tone={s.tone}
      title={lastPingAt ? `Last ping: ${new Date(lastPingAt).toLocaleString()}` : "No pings recorded"}
      className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest"
      style={{ color: s.color }}
    >
      <span
        style={{
          width: 10, height: 10, borderRadius: "50%", background: s.color,
          boxShadow: s.tone === "live" ? `0 0 8px ${s.color}, 0 0 3px ${s.color}` : "none",
          animation: s.tone === "live" ? "kioskHeartbeatPulse 1.6s ease-in-out infinite" : undefined,
        }}
      />
      <span>{s.label}</span>
    </span>
  );
}

export default function KioskLinksPage() {
  const [links, setLinks] = useState([]);
  const [zones, setZones] = useState([]);
  const [walls, setWalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ label: "", hw: "chromeos", res: "1080p", zone: "", notes: "", wall_id: "default" });

  const publicBase = typeof window !== "undefined" ? window.location.origin : "";

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [linksR, zonesR, wallsR] = await Promise.all([
        adminApi.get("/admin/kiosk-wall/links"),
        adminApi.get("/public/kiosk-wall/zones"),
        adminApi.get("/admin/kiosk-wall/walls"),
      ]);
      setLinks(linksR.data?.links || []);
      setZones(zonesR.data?.zones || []);
      setWalls(wallsR.data?.walls || []);
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // v2.7.61+E44 · Auto-refresh every 30s so the heartbeat traffic-light
  // dots age in near-real-time without the admin manually clicking Reload.
  useEffect(() => {
    const id = setInterval(reload, 30000);
    return () => clearInterval(id);
  }, [reload]);

  // Force the heartbeat traffic-light re-render every 15s so the age
  // label ticks even when no new API data arrived.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, []);

  const create = async () => {
    if (!form.label.trim()) { toast.error("Label required"); return; }
    setCreating(true);
    try {
      await adminApi.post("/admin/kiosk-wall/links", form);
      toast.success("Provisioned link created");
      setForm({ label: "", hw: "chromeos", res: "1080p", zone: "", notes: "", wall_id: form.wall_id || "default" });
      await reload();
    } catch (e) {
      toast.error(`Create failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (linkId, label) => {
    if (!window.confirm(`Revoke "${label}"? The kiosk will render "Access Revoked" on its next heartbeat (within 60s).`)) return;
    try {
      await adminApi.delete(`/admin/kiosk-wall/links/${encodeURIComponent(linkId)}`);
      toast.success("Link revoked");
      await reload();
    } catch (e) {
      toast.error(`Revoke failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const patch = async (linkId, field, value) => {
    try {
      await adminApi.patch(`/admin/kiosk-wall/links/${encodeURIComponent(linkId)}`, { [field]: value });
      toast.success(`Updated ${field} → ${value}`);
      await reload();
    } catch (e) {
      toast.error(`Update failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const copyLink = (link) => {
    const url = publicBase + link.kiosk_url_path;
    navigator.clipboard?.writeText(url);
    toast.success("Kiosk URL copied to clipboard");
  };

  const downloadMacApp = async (token) => {
    try {
      // Adds ?token=... so the .app bundle auto-authenticates when opened.
      const res = await adminApi.get(`/admin/kiosk-wall/macos-launcher${token ? `?token=${encodeURIComponent(token)}` : ""}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "BullerDispatchWall.app.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("macOS .app.zip downloaded");
    } catch (e) {
      toast.error(`Download failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0c0d10] text-slate-900 dark:text-zinc-100 p-4 sm:p-6 lg:p-8" data-testid="kiosk-links-page">
      {/* v2.7.61+E44 · scoped pulse animation for the "Live" heartbeat dot */}
      <style>{`@keyframes kioskHeartbeatPulse{
        0%,100% { transform: scale(1);   opacity: 1; }
        50%     { transform: scale(1.35); opacity: 0.75; }
      }`}</style>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin/developer/dispatch-video-wall" className="text-amber-400 hover:text-amber-300 flex items-center gap-1 text-sm font-bold uppercase tracking-wider" data-testid="back-to-wall">
              <ArrowLeft className="h-4 w-4"/>Video Wall
            </Link>
            <div className="h-6 w-px bg-zinc-800"/>
            <Link2 className="h-6 w-6 text-indigo-300"/>
            <h1 className="text-2xl font-extrabold tracking-tight">Provisioned Kiosk Links</h1>
            <Badge className="bg-indigo-500/20 text-indigo-200 border-indigo-400/40 text-[10px] uppercase tracking-widest">Developer · §6</Badge>
          </div>
          <div className="flex gap-2">
            <Button onClick={reload} variant="ghost" className="text-zinc-300" data-testid="links-reload"><RefreshCw className="h-4 w-4 mr-1.5"/>Reload</Button>
            <Button onClick={() => downloadMacApp(null)} className="bg-slate-800 text-white hover:bg-slate-700 font-bold" data-testid="download-macos-generic">
              <Download className="h-4 w-4 mr-1.5"/>Download macOS Launcher (generic)
            </Button>
          </div>
        </div>

        {/* Create form */}
        <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4" data-testid="link-create-card">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-extrabold pb-1.5 border-b border-amber-400/40 flex items-center gap-2">
            <Plus className="h-3.5 w-3.5"/>Provision new kiosk URL
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Label</label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Dispatch Room · East wall Chromebox" className="bg-zinc-950 border-zinc-700" data-testid="link-label-input" maxLength={80}/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Hardware profile</label>
              <Select value={form.hw} onValueChange={(v) => setForm({ ...form, hw: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700" data-testid="link-hw-select"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-700">
                  {Object.entries(HW_LABELS).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`link-hw-${v}`}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Resolution</label>
              <Select value={form.res} onValueChange={(v) => setForm({ ...form, res: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700" data-testid="link-res-select"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-700">
                  {Object.entries(RES_LABELS).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`link-res-${v}`}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Default zone (optional)</label>
              <Select value={form.zone || "__all__"} onValueChange={(v) => setForm({ ...form, zone: v === "__all__" ? "" : v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700" data-testid="link-zone-select"><SelectValue placeholder="All zones"/></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-700">
                  <SelectItem value="__all__">All zones</SelectItem>
                  {zones.map((z) => <SelectItem key={z.id} value={z.id} data-testid={`link-zone-${z.id}`}>{z.id} · {z.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Wall instance (§1 multi-wall)</label>
              <Select value={form.wall_id || "default"} onValueChange={(v) => setForm({ ...form, wall_id: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700" data-testid="link-wall-select"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-700">
                  {walls.map((w) => <SelectItem key={w.wall_id} value={w.wall_id} data-testid={`link-wall-${w.wall_id}`}>{w.wall_id === "default" ? "default (main)" : w.wall_id}</SelectItem>)}
                  {!walls.some((w) => w.wall_id === form.wall_id) && form.wall_id && (
                    <SelectItem value={form.wall_id}>{form.wall_id} (new)</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Notes (optional)</label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Serial · Location · Contact" className="bg-zinc-950 border-zinc-700" data-testid="link-notes-input" maxLength={240}/>
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button onClick={create} disabled={creating || !form.label.trim()} className="bg-amber-400 text-zinc-900 hover:bg-amber-300 font-bold w-full" data-testid="link-create-btn">
                <Plus className="h-4 w-4 mr-2"/>{creating ? "Provisioning…" : "Provision Link"}
              </Button>
            </div>
          </div>
        </Card>

        {/* Links list */}
        <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3" data-testid="links-list-card">
          <div className="flex items-center justify-between flex-wrap gap-2 pb-1.5 border-b border-sky-400/40">
            <div className="text-[10px] uppercase tracking-widest text-sky-300 font-bold flex items-center gap-2">
              <MonitorPlay className="h-3.5 w-3.5"/>Provisioned Links ({links.length})
            </div>
            {/* Heartbeat legend */}
            <div className="flex items-center gap-3 text-[9px] uppercase tracking-widest font-bold" data-testid="heartbeat-legend">
              <span className="text-zinc-500">Heartbeat</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_6px_#22c55e]"/><span className="text-emerald-300">Live &lt; 60s</span></span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"/><span className="text-amber-300">Stalling &lt; 5m</span></span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"/><span className="text-red-300">Dead</span></span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-zinc-700"/><span className="text-zinc-500">Never</span></span>
            </div>
          </div>
          {loading && <div className="text-xs text-zinc-500">Loading…</div>}
          {!loading && links.length === 0 && <div className="text-xs text-zinc-500">No links yet — provision one above.</div>}
          <div className="space-y-2">
            {links.map((l) => {
              const fullUrl = publicBase + l.kiosk_url_path;
              return (
                <div key={l.id} className={`bg-zinc-950 border rounded-lg p-3 ${l.active ? "border-zinc-800" : "border-red-900/50"}`} data-testid={`link-row-${l.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-bold text-zinc-100 truncate" data-testid={`link-label-${l.id}`}>{l.label}</div>
                        {!l.active && <Badge className="bg-red-500/20 text-red-200 border-red-400/40 text-[9px]">REVOKED</Badge>}
                        <Badge className="bg-slate-800 border-slate-700 text-slate-200 text-[9px]">{HW_LABELS[l.hw] || l.hw}</Badge>
                        <Badge className="bg-slate-800 border-slate-700 text-slate-200 text-[9px]">{RES_LABELS[l.res] || l.res}</Badge>
                        {l.zone && <Badge className="bg-amber-500/20 text-amber-200 border-amber-400/40 text-[9px]">{l.zone}</Badge>}
                        {l.wall_id && l.wall_id !== "default" && <Badge className="bg-indigo-500/20 text-indigo-200 border-indigo-400/40 text-[9px]" data-testid={`link-wall-${l.id}`}>WALL · {l.wall_id}</Badge>}
                        <HeartbeatDot lastPingAt={l.last_ping_at} testid={`link-heartbeat-${l.id}`}/>
                      </div>
                      {l.notes && <div className="text-[10px] text-zinc-500 mt-1">{l.notes}</div>}
                      <div className="mt-1.5 flex items-center gap-2">
                        <code className="text-[10px] text-amber-300 font-mono break-all" data-testid={`link-url-${l.id}`}>{fullUrl}</code>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <Button size="sm" onClick={() => copyLink(l)} className="bg-sky-500 text-white hover:bg-sky-400 h-7 text-[10px] uppercase tracking-wider" data-testid={`link-copy-${l.id}`}>
                        <Copy className="h-3 w-3 mr-1"/>Copy
                      </Button>
                      <Button size="sm" onClick={() => patch(l.id, "res", l.res === "1080p" ? "4k" : "1080p")} disabled={!l.active}
                              className="bg-zinc-800 text-zinc-100 hover:bg-zinc-700 h-7 text-[10px] uppercase tracking-wider" data-testid={`link-toggle-res-${l.id}`}>
                        → {l.res === "1080p" ? "4K" : "1080p"}
                      </Button>
                      <Button size="sm" onClick={() => downloadMacApp(l.token)} disabled={!l.active}
                              className="bg-slate-800 text-white hover:bg-slate-700 h-7 text-[10px] uppercase tracking-wider" data-testid={`link-mac-${l.id}`}>
                        <Download className="h-3 w-3 mr-1"/>macOS .app
                      </Button>
                      {l.active && (
                        <Button size="sm" onClick={() => revoke(l.id, l.label)} className="bg-red-600 text-white hover:bg-red-500 h-7 text-[10px] uppercase tracking-wider" data-testid={`link-revoke-${l.id}`}>
                          <Trash2 className="h-3 w-3 mr-1"/>Revoke
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
